import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { SequenceAnimator } from 'react-sequence-animator';
import { navigate } from '@gatsbyjs/reach-router';

import {
  appLogin,
  appLogout,
  setLoginRedirectRoute,
} from '@store/appStateSlice';
import { profileReset, fetchProfile } from '@store/userProfileSlice';
import { setAccessToken, removeAccessToken } from '@utils';
import SEO from '@common/components/SEO/SEO';
import styles from './Login.module.scss';
import metadata from './metadata.json';
import LogoSequence from '@components/LogoSequence';
import { LUCID_LOGO_ANIMATION_ENABLED } from '@constants';
import { getLocalizedContent as l } from '@helpers/languages';
import { getLanguagePrefix, getGigyaLocale } from '@helpers';
import PortalContainer from '@components/PortalContainer';
import { documentsReset } from '@store/documentsSlice/documentsSlice';

const Login = props => {
  const dispatch = useDispatch();
  const {
    isLoggedIn,
    hasGigyaLoaded,
    loginRedirectRoute,
    language,
    prefixLanguage,
    direction,
  } = useSelector(({ appState }) => appState);

  const isRedirect = new URL(window.location.href).searchParams.get('redirect');

  const [animatedOnce, setAnimatedOnce] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const sequenceChildren = LogoSequence.map((imageURL, index) => (
    <img
      key={index}
      className={styles.loader}
      src={imageURL}
      alt={`animation sequence frame ${index}`}
    />
  ));
  const [startScreen, setStartScreen] = useState('gigya-login-screen');

  const onLogin = async () => {
    gigya.accounts.getJWT({
      fields: 'profile.email',
      expiration: 21600,
      callback: response => {
        const token = response.id_token;

        if (token) {
          removeAccessToken();
          setAccessToken(token);
          dispatch(appLogin(token));
          dispatch(fetchProfile(token));
        }
      },
    });
  };

  useEffect(() => {
    if (isRedirect) {
      dispatch(setLoginRedirectRoute('/commerce/authenticate/bigcommerce'));
    }
  }, [isRedirect]);

  useEffect(() => {
    // Post-login.
    if (isLoggedIn) {
      if (loginRedirectRoute) {
        navigate(loginRedirectRoute, { replace: true }).then(() =>
          dispatch(setLoginRedirectRoute(''))
        );
      } else {
        window.location.assign(
          `${window.location.origin}${getLanguagePrefix(
            prefixLanguage
          )}/portal/dashboard`
        );
      }
    }
  }, [isLoggedIn]);

  const showLoginForm = () => {
    const gigyaLocale = getGigyaLocale(language);
    gigya.accounts.showScreenSet({
      screenSet: 'Default-RegistrationLogin',
      startScreen: startScreen,
      containerID: 'gigya_container_id',
      lang: gigyaLocale,
    });

    gigya.accounts.addEventHandlers({
      onLogin: onLogin,
    });
  };

  const handleLogout = () => {
    gigya.accounts.logout({
      callback: () => {
        removeAccessToken();
        dispatch(appLogout());
        dispatch(profileReset());
        dispatch(documentsReset());
      },
    });
  };

  useEffect(() => {
    const query = new URLSearchParams(
      location?.search || window?.location?.search
    );
    const queryApiKey = query.get('apiKey');
    const queryPage = query.get('page');
    if (queryApiKey) {
      // if ?apiKey query param exists, display Reset Password page. Gigya reads the value.
      setStartScreen('gigya-reset-password-screen');
    } else if (queryPage == 'registration') {
      // if ?page=registration query param exists, display Registration page
      setStartScreen('Create-Account');
    } else if (queryPage == 'forgotpassword') {
      // if ?page=forgotpassword query param exists, display Forgot Password page
      setStartScreen('gigya-forgot-password-screen');
    } else {
      // load the default login screen
      setStartScreen('gigya-login-screen');
    }

    if ((!LUCID_LOGO_ANIMATION_ENABLED || animatedOnce) && hasGigyaLoaded) {
      if (!isLoggedIn) {
        handleLogout();
        showLoginForm();
      }
      setLoading(false);
    }
  }, [animatedOnce, hasGigyaLoaded]);

  return (
    <PortalContainer isLoggedOut>
      <SEO
        title={l(metadata.title, language)}
        description={l(metadata.description, language)}
        location={props.location}
      />
      <motion.div
        layout="position"
        key="designs-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ delay: 0.25, duration: 0.25 }}
      >
        <div className={styles.PortalLogin}>
          <AnimatePresence>
            <motion.div
              initial={{ y: -40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -40, opacity: 0 }}
              transition={{ delay: 0.25, duration: 1 }}
            >
              <div id="gigya_container_id" dir={direction} />
            </motion.div>
          </AnimatePresence>

          {LUCID_LOGO_ANIMATION_ENABLED && (
            <div className={styles.loaderWrapper}>
              {isLoading && (
                <SequenceAnimator
                  duration={3500}
                  onSequenceEnd={() => setAnimatedOnce(true)}
                />
              )}
            </div>
          )}
        </div>
      </motion.div>
    </PortalContainer>
  );
};

export default Login;
